import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Package } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSpareParts } from "@/hooks/useSpareParts";
import { isInStock } from "@/lib/partStatus";
import { useAllStorageBins } from "@/hooks/useStorageBins";
import { useResalePricePctMap } from "@/hooks/useResalePricePct";
import { groupPartTypes } from "@/lib/partTypes";

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d > 0) return `${d}d ago`;
  const h = Math.floor(diff / 3600000);
  if (h > 0) return `${h}h ago`;
  const m = Math.floor(diff / 60000);
  return `${Math.max(1, m)}m ago`;
}

export default function PartTypeDetail() {
  const { t } = useTranslation("inventory");
  const navigate = useNavigate();
  const { partTypeId } = useParams();

  const { data: spareParts = [] } = useSpareParts();
  const { data: bins = [] } = useAllStorageBins();
  const companyIds = useMemo(
    () => Array.from(new Set((spareParts || []).map((p: any) => p.company_id).filter(Boolean) as string[])),
    [spareParts],
  );
  const resalePctMap = useResalePricePctMap(companyIds);

  const partType = useMemo(
    () => groupPartTypes(spareParts.filter((p) => isInStock(p.status))).find((p) => p.id === partTypeId),
    [spareParts, partTypeId],
  );

  const applianceIds = useMemo(
    () => Array.from(new Set((partType?.units || []).map((u) => u.appliance_id).filter(Boolean) as string[])),
    [partType],
  );

  const { data: appliances = [] } = useQuery({
    queryKey: ["appliances_for_part_type", applianceIds.join(",")],
    enabled: applianceIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appliances")
        .select("id, brand, model_number, serial_number")
        .in("id", applianceIds);
      if (error) throw error;
      return data || [];
    },
  });

  const applianceById = useMemo(
    () => new Map(appliances.map((a: any) => [a.id, a])),
    [appliances],
  );
  const binById = useMemo(() => new Map(bins.map((b) => [b.id, b])), [bins]);

  if (!partType) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <div className="px-6 pt-8 pb-6">
          <nav className="text-xs text-muted-foreground mb-6">
            <button onClick={() => navigate("/inventory?tab=parts")} className="hover:text-foreground">
              Inventory
            </button>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">Part type</span>
          </nav>
          <div className="bg-card rounded-xl border border-border p-8 text-center text-muted-foreground">
            Part type not found.
          </div>
        </div>
      </div>
    );
  }

  // Bin distribution
  const binCounts = new Map<string, number>();
  let awaiting = 0;
  partType.units.forEach((u) => {
    if (u.bin_id) binCounts.set(u.bin_id, (binCounts.get(u.bin_id) || 0) + 1);
    else awaiting += 1;
  });

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        {/* Breadcrumb */}
        <nav className="text-xs text-muted-foreground mb-3">
          <button onClick={() => navigate("/inventory?tab=parts")} className="hover:text-foreground">
            Inventory
          </button>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">{partType.name}</span>
        </nav>

        <div className="flex items-start gap-3 mb-2">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Package className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-foreground">{partType.name}</h1>
            <p className="text-sm font-mono text-muted-foreground">
              {partType.oem_part_number || "—"}
              {partType.brand ? ` · ${partType.brand}` : ""}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold">
              {t("status.countInStock", { count: partType.stock })}
            </span>
            {awaiting > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                {t("status.countNotInBin", { count: awaiting })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Two-column body */}
      <div className="px-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="md:col-span-3 space-y-4">
          <div className="bg-card rounded-xl border border-border p-4">
            <h2 className="text-sm font-semibold text-foreground mb-3">Compatible models</h2>
            {partType.compatible_models.length === 0 ? (
              <p className="text-sm text-muted-foreground">No compatibility data yet.</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {partType.compatible_models.map((m) => (
                  <li key={m} className="px-2.5 py-1 rounded-md bg-muted text-foreground text-xs font-mono">
                    {m}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <h2 className="text-sm font-semibold text-foreground mb-3">Where it's stored</h2>
            <div className="flex flex-wrap gap-2">
              {Array.from(binCounts.entries()).map(([binId, count]) => {
                const bin = binById.get(binId);
                if (!bin) return null;
                return (
                  <button
                    key={binId}
                    onClick={() => navigate(`/bin/${binId}`)}
                    className="px-2.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-mono hover:bg-primary/20 transition-colors"
                  >
                    {count} in {bin.address}
                  </button>
                );
              })}
              {awaiting > 0 && (
                <span className="px-2.5 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-medium">
                  {t("status.countNotInBin", { count: awaiting })}
                </span>
              )}
              {binCounts.size === 0 && awaiting === 0 && (
                <p className="text-sm text-muted-foreground">Not stored anywhere.</p>
              )}
            </div>
          </div>
        </div>

        <div className="md:col-span-2 space-y-4">
          <div className="bg-card rounded-xl border border-border p-4">
            <h2 className="text-sm font-semibold text-foreground mb-3">{t("catalogue")}</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">OEM number</dt>
                <dd className="font-mono text-foreground">{partType.oem_part_number || "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Brand</dt>
                <dd className="text-foreground">{partType.brand || "—"}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Units list */}
      <div className="px-6 mt-6">
        <h2 className="text-sm font-semibold text-foreground mb-3">
          Units in stock ({partType.stock})
        </h2>
        <ul className="space-y-2">
          {partType.units.map((u) => {
            const bin = u.bin_id ? binById.get(u.bin_id) : null;
            const appliance = u.appliance_id ? applianceById.get(u.appliance_id) : null;
            const label = u.serial_no || u.qr_token;
            return (
              <li key={u.id}>
                <button
                  onClick={() => navigate(`/spare-part/${u.serial_no || u.qr_token}`)}
                  className="w-full text-left bg-card rounded-xl border border-border p-3 flex items-center gap-3 transition-all hover:border-primary hover:shadow-md"
                >
                  <span className="font-mono text-xs text-foreground w-44 truncate">{label}</span>
                  {bin ? (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/bin/${bin.id}`);
                      }}
                      className="px-2 py-0.5 rounded-md bg-primary/10 text-primary font-mono text-xs"
                    >
                      {bin.address}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-xs">
                      {t("status.notInBin")}
                    </span>
                  )}
                  <span className="flex-1 min-w-0 text-xs text-muted-foreground truncate">
                    {appliance
                      ? `${appliance.brand || ""} · ${appliance.model_number || ""}${
                          appliance.serial_number ? ` · ${appliance.serial_number}` : ""
                        }`
                      : "Unknown source"}
                  </span>
                  {u.estimated_value != null && (
                    <span className="text-xs text-muted-foreground shrink-0 tabular-nums" title="List · Est. re-use">
                      €{Number(u.estimated_value).toFixed(2)} <span className="text-emerald-700 dark:text-emerald-400">· €{(Number(u.estimated_value) * (resalePctMap.get(u.company_id) ?? 0.5)).toFixed(2)}</span>
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground shrink-0">
                    {timeAgo(u.harvested_at)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
